import * as React from "react";
import {
  useMappedState,
  useDispatch
} from "redux-react-hook";
import {
  Responsive,
  WidthProvider,
  Layouts as GridLayouts,
  Layout as GridLayout
} from "react-grid-layout";
import * as _ from "lodash";

import { OrdersWeekSummaryDashboardWidget } from "src/core/orders/components/OrdersWeekSummaryDashboardWidget";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import type { IStore } from "src/core/shared/store";
import { useDrop } from "src/core/drops/hooks";
import { PageProps } from "src/pages/Router/routes";
import DashboardPromotionsBanner from "src/modules/promotions/components/DashboardPromotionsBanner";
import { DropPromotionsProvider } from "src/modules/promotions/context/DropPromotionsContext";
import "react-grid-layout/css/styles.css";
import "react-grid-layout/node_modules/react-resizable/css/styles.css";
import NotificationDashboardWidget from "src/core/notifications/components/NotificationDashboardWidget";
import UpcomingPaymentsDashboardWidget from "src/modules/payment/components/UpcomingPaymentsDashboardWidget/UpcomingPaymentsDashboardWidget";
import WidgetContainer from "src/library/components/WidgetContainer/WidgetContainer";
import { ReactGridLayoutStyles } from "src/library/components/GlobalStyles/ReactGridLayoutStyles";
import TransactionsDashboardWidget from "src/modules/payment/components/TransactionsDashboardWidget/TransactionsDashboardWidget";
import { getPaymentRequired } from "src/core/drops/utils/inherited-settings";
import useLocalStorage from "src/library/hooks/useLocalStorage";
import { toggleNotifications } from "src/core/notifications/store/notifications.actions";

const ResponsiveGridLayout = WidthProvider(Responsive);

const Dashboard: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { installedModules, masqueradeMode } = useMappedState(({ global }: IStore) => ({
    installedModules: global.settings ? global.settings.installedModules : [],
    masqueradeMode: global.masqueradeMode
  }));

  const [ { drop } ] = useDrop();
  const dispatch = useDispatch();
  const [ lsDashboardLayout, storeLsDashboardLayout ] = useLocalStorage("customer-dashoard-layout");
  const [ layouts, setlayouts ] = React.useState<GridLayouts>(lsDashboardLayout ? JSON.parse(lsDashboardLayout) : initialLayouts);

  // whether to diaply the payment widgets or not
  const hasPaymentWidgets = React.useMemo(() => {
    if (drop?.groupedSettings) {
      const paymentRequired = getPaymentRequired(drop.groupedSettings);

      if (paymentRequired?.required && !masqueradeMode?.active) {
        return true;
      }
    }

    return false;
  }, [ drop?.groupedSettings ]);

  const handleLayoutChange = React.useCallback((current: GridLayout[], allLayouts: GridLayouts) => {
    if (hasPaymentWidgets) {
      const hasPaymentWidgets = _.find(current, item => item.i === "transactions");

      if (!hasPaymentWidgets || hasPaymentWidgets.h === 1) {
        // reset to the initial layout if it gets messed up by the payment widgets
        setlayouts(initialLayouts);

        return;
      }
    }

    storeLsDashboardLayout(JSON.stringify(allLayouts));
    setlayouts(allLayouts);
  }, [ hasPaymentWidgets, storeLsDashboardLayout ]);

  if (!installedModules.length || !drop) {
    return null;
  }

  return (
    <Layout
      {...props}
      title={null}
    >
      <MainContentWrapper fill>
        <ReactGridLayoutStyles />

        <ResponsiveGridLayout
          allowOverlap={false}
          autoSize
          containerPadding={[ 0, 0 ]}
          draggableHandle=".widget-drag"
          breakpoints={{
            lg: 900,
            md: 550,
            sm: 400
          }}
          cols={{
            lg: 3,
            md: 2,
            sm: 1
          }}
          onLayoutChange={handleLayoutChange}
          className="layout"
          layouts={layouts}
          compactType="vertical"
          rowHeight={30}
        >
          <div key="orders-summary">
            <WidgetContainer
              title="Weekly summary"
              action={{
                title: "View all orders",
                action: () => props.history.push("/orders", { ordersViewType: "table" })
              }}
            >
              <OrdersWeekSummaryDashboardWidget
                date={new Date()}
                week={new Date()}
              />
            </WidgetContainer>
          </div>

          <div key="notifications">
            <WidgetContainer
              title="Notifications"
              action={{
                title: "View all",
                action: () => {
                  dispatch(toggleNotifications(true, null));
                }
              }}
            >
              <NotificationDashboardWidget />
            </WidgetContainer>
          </div>

          {hasPaymentWidgets && (
            <div key="upcoming-payments">
              <WidgetContainer
                title="Upcoming payments"
                action={{
                  title: "View billing details",
                  action: () => props.history.push("/settings/user#payment_methods")
                }}
              >
                <UpcomingPaymentsDashboardWidget />
              </WidgetContainer>
            </div>
          )}

          {hasPaymentWidgets && (
            <div key="transactions">
              <WidgetContainer
                title="Recent Payments"
                action={{
                  title: "View all",
                  action: () => props.history.push("/settings/user#transactions")
                }}
              >
                <TransactionsDashboardWidget />
              </WidgetContainer>
            </div>
          )}

          {installedModules.includes("promotion") && (
            <div key="promotions">
              <DropPromotionsProvider dropId={drop.id}>
                <DashboardPromotionsBanner />
              </DropPromotionsProvider>
            </div>
          )}

        </ResponsiveGridLayout>

      </MainContentWrapper>
    </Layout>
  );
};

export default Dashboard;

/**
 * this is used to setup the initial positions of the dashboard widgets across screen sizes
 * @see {GridLayouts}
 */
const initialLayouts: GridLayouts = {
  lg: [
    {
      i: "orders-summary",
      x: 0,
      y: 0,
      w: 2,
      h: 10,
      minH: 10,
      maxH: 10,
      minW: 2,
      isResizable: true
    },
    {
      i: "notifications",
      x: 8,
      y: 8,
      w: 1,
      h: 10,
      minH: 6,
      maxH: 16,
      maxW: 1,
      isResizable: true
    },
    {
      i: "upcoming-payments",
      x: 0,
      y: 8,
      w: 2,
      h: 10,
      minH: 6,
      maxH: 10,
      minW: 2,
      isResizable: true
    },
    {
      i: "transactions",
      x: 2,
      y: 8,
      w: 1,
      h: 10,
      minH: 6,
      maxH: 16,
      minW: 1,
      isResizable: true
    },
    {
      i: "promotions",
      x: 0,
      y: 16,
      w: 3,
      h: 8,
      minH: 8,
      maxH: 8,
      minW: 3,
      isResizable: false
    }
  ],
  md: [
    {
      i: "orders-summary",
      x: 0,
      y: 0,
      w: 2,
      h: 10,
      minH: 10,
      minW: 2,
      isResizable: false
    },
    {
      i: "upcoming-payments",
      x: 0,
      y: 9,
      w: 2,
      h: 10,
      minH: 10,
      minW: 2,
      isResizable: false
    },
    {
      i: "transactions",
      x: 0,
      y: 12,
      w: 2,
      h: 10,
      minH: 10,
      minW: 2,
      isResizable: false
    },
    {
      i: "notifications",
      x: 0,
      y: 17,
      w: 2,
      h: 10,
      minW: 2,
      minH: 8,
      maxH: 16,
      isResizable: true
    },

    {
      i: "promotions",
      x: 0,
      y: 23,
      w: 2,
      h: 8,
      minH: 8,
      minW: 2,
      isResizable: false
    }
  ],
  sm: [
    {
      i: "orders-summary",
      x: 0,
      y: 0,
      w: 1,
      h: 23,
      minH: 23,
      minW: 1,
      isResizable: false
    },
    {
      i: "upcoming-payments",
      x: 0,
      y: 23,
      w: 1,
      h: 10,
      minH: 10,
      minW: 1,
      isResizable: false
    },
    {
      i: "notifications",
      x: 0,
      y: 31,
      w: 1,
      h: 10,
      minH: 10,
      minW: 1,
      isResizable: false
    },
    {
      i: "transactions",
      x: 0,
      y: 36,
      w: 1,
      h: 10,
      minH: 10,
      minW: 1,
      isResizable: false
    },
    {
      i: "promotions",
      x: 0,
      y: 40,
      w: 1,
      h: 8,
      minH: 8,
      minW: 1,
      isResizable: false
    }
  ]
};
